import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loading } from "../components/Loader";
import { Select } from "../components/Select";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Barcode from "react-barcode";
import {
  deleteProduct,
  fetchProductDetail,
  selectDeleteProductStatusById,
  selectProductById,
  selectProductErrorById,
  selectProductStatusById,
  selectUpdateProductErrorById,
  selectUpdateProductStatusById,
  updateProduct,
} from "../features/products/productsSlice";
import ProductImageGallery from "../components/ProductImageGallery";
import ConfirmModal from "../components/ConfirmModal";
import CategoryMultiSelect from "../components/CategoryMultiSelect";

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("ru-RU");
}

function normalizeNumberInput(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  // Allow comma decimal separator
  return raw.replace(",", ".");
}

function asStringList(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((x) => (x == null ? "" : String(x).trim()))
    .filter(Boolean);
}

function uniqStrings(list) {
  const out = [];
  const seen = new Set();
  for (const v of Array.isArray(list) ? list : []) {
    const s = String(v || "").trim();
    if (!s) continue;
    if (seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out;
}

function BarcodePreview({ value, onDelete }) {
  const v = String(value || "").trim();
  if (!v) return null;
  return (
    <div style={styles.barcodeCard} title={v}>
      <div style={styles.barcodeItemRow}>
        <div style={styles.barcodeWhite}>
          <Barcode value={v} height={56} width={2} margin={4} displayValue background="#ffffff" lineColor="#000000" />
        </div>
        {typeof onDelete === "function" && (
          <button type="button" onClick={() => onDelete(v)} style={styles.barcodeDeleteBtn} title="Удалить штрихкод">
            ✕
          </button>
        )}
      </div>
    </div>
  );
}

export default function ProductEditPage() {
  const { productId } = useParams();
  const id = String(productId);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const product = useSelector(selectProductById(id));
  const loadStatus = useSelector(selectProductStatusById(id));
  const loadError = useSelector(selectProductErrorById(id));
  const saveStatus = useSelector(selectUpdateProductStatusById(id));
  const saveError = useSelector(selectUpdateProductErrorById(id));
  const deleteStatus = useSelector(selectDeleteProductStatusById(id));


  const [form, setForm] = useState({
    name: "",
    barcode: "",
    description: "",
    cost_price: "",
    wholesale_price: "",
    self_cost_price: "",
    is_quick: false,
    is_active: true,
    show_in_cashback: false,
  });

  const [selectedCategories, setSelectedCategories] = useState([]); // [{id,name}]

  const [extraBarcodes, setExtraBarcodes] = useState([]);
  const [newBarcode, setNewBarcode] = useState("");
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchProductDetail({ productId: id }));
  }, [dispatch, id]);

  useEffect(() => {
    if (!product) return;
    setForm({
      name: product.name ?? "",
      barcode: product.barcode ?? "",
      description: product.description ?? "",
      cost_price: product.cost_price ?? "",
      wholesale_price: product.wholesale_price ?? "",
      self_cost_price: product.self_cost_price ?? "",
      is_quick: Boolean(product.is_quick),
      is_active: Boolean(product.is_active),
      show_in_cashback: Boolean(product.show_in_cashback),
    });

    const cats = Array.isArray(product?.categories) ? product.categories : [];
    setSelectedCategories(
      cats
        .map((c) => ({ id: c?.id, name: c?.name }))
        .filter((c) => c.id != null)
        .map((c) => ({ id: c.id, name: c.name || `#${c.id}` }))
    );

    const allBarcodes = asStringList(product?.barcodes);
    const primary = String(product?.barcode || "").trim();
    const extras = allBarcodes.filter((b) => b && b !== primary);
    setExtraBarcodes(uniqStrings(extras));
  }, [product]);

  const categoryNames = useMemo(
    () => (selectedCategories || []).map((c) => c?.name).filter(Boolean),
    [selectedCategories]
  );
  const barcodes = useMemo(() => {
    const primary = String(form.barcode || "").trim();
    const extras = uniqStrings(extraBarcodes);
    return uniqStrings([primary, ...extras]);
  }, [extraBarcodes, form.barcode]);

  const canSave = saveStatus !== "saving" && loadStatus === "succeeded";

  // --- Print label helpers ---
  const printBarcode = useMemo(() => String(form.barcode || product?.barcode || "").trim(), [form.barcode, product?.barcode]);
  const printName = useMemo(() => String(form.name || product?.name || "").trim(), [form.name, product?.name]);
  const retailPrice = useMemo(() => {
    const v = Number(form.cost_price ?? product?.cost_price);
    return Number.isFinite(v) ? v : null;
  }, [form.cost_price, product?.cost_price]);
  const wholesalePrice = useMemo(() => {
    const v = Number(form.wholesale_price ?? product?.wholesale_price);
    return Number.isFinite(v) ? v : null;
  }, [form.wholesale_price, product?.wholesale_price]);
  const canPrint = Boolean(printBarcode) && loadStatus === "succeeded";

  // CRITICAL: Force qty=1 always (browser keeps old values like 5)
  const [printSettings, setPrintSettings] = useState({ widthMm: 38, heightMm: 30, qty: 1 });

  // Force reset qty to 1 on every render if it's not 1
  useEffect(() => {
    if (printSettings.qty !== 1) {
      setPrintSettings((s) => ({ ...s, qty: 1 }));
    }
  });

  // Reset on product change
  useEffect(() => {
    setPrintSettings({ widthMm: 50, heightMm: 40, qty: 1 });
    setLabelLayout({ orientation: "portrait" });
  }, [id]);

  const getContentStyle = (orientation, isPrint = false) => {
    const isLandscape = orientation === "landscape";
    return {
      width: "100%",
      height: "100%",
      transform: isLandscape ? "rotate(90deg)" : "none",
      transformOrigin: "center center",
      padding: isPrint ? "3mm" : 8,
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    };
  };

  const printWidthMm = useMemo(() => {
    const n = Number(printSettings.widthMm);
    if (!Number.isFinite(n)) return 58;
    return Math.min(120, Math.max(20, Math.round(n)));
  }, [printSettings.widthMm]);
  const printHeightMm = useMemo(() => {
    const n = Number(printSettings.heightMm);
    if (!Number.isFinite(n)) return 40;
    return Math.min(120, Math.max(15, Math.round(n)));
  }, [printSettings.heightMm]);
  const printQty = useMemo(() => {
    const n = Number(printSettings.qty);
    if (!Number.isFinite(n)) return 1;
    return Math.min(100, Math.max(1, Math.round(n)));
  }, [printSettings.qty]);

  const [layoutModalOpen, setLayoutModalOpen] = useState(false);
  const [printNowQty, setPrintNowQty] = useState(null);
  const printPendingRef = useRef(false);

  const [labelLayout, setLabelLayout] = useState({
    orientation: "portrait", // "portrait" or "landscape"
  });


  const printCss = useMemo(() => {
    const isPortrait = printHeightMm >= printWidthMm;
    const orientation = isPortrait ? "portrait" : "landscape";
    return `
      @page { size: ${printWidthMm}mm ${printHeightMm}mm ${orientation}; margin: 0; }
      @media print {
        html, body { margin: 0 !important; padding: 0 !important; }
        body > *:not(#print-label-root) { display: none !important; }
        #print-label-root {
          display: block !important;
          position: static !important;
          margin: 0 !important; padding: 0 !important;
          width: ${printWidthMm}mm !important;
          height: auto !important;
          overflow: visible !important;
        }
        .print-label {
          display: block !important;
          position: relative !important;
          margin: 0 !important; padding: 0 !important;
          width: ${printWidthMm}mm !important;
          height: ${printHeightMm}mm !important;
          overflow: hidden !important;
          box-sizing: border-box !important;
          page-break-before: always !important;
          break-before: page !important;
        }
        .print-label:first-child {
          page-break-before: auto !important;
          break-before: auto !important;
        }
      }
    `;
  }, [printHeightMm, printWidthMm]);

  const onPrintLabel = () => {
    if (!canPrint) return;
    printPendingRef.current = true;
    setPrintNowQty(printQty);
  };

  useEffect(() => {
    if (printNowQty === null) return;
    const t = setTimeout(() => {
      window.print();
      setPrintNowQty(null);
    }, 100);
    return () => clearTimeout(t);
  }, [printNowQty]);

  const addBarcode = () => {
    const s = String(newBarcode || "").trim();
    if (!s) return;
    setExtraBarcodes((prev) => uniqStrings([...(prev || []), s]));
    setNewBarcode("");
  };

  const deleteBarcode = (barcode) => {
    const bc = String(barcode || "").trim();
    if (!bc) return;
    const primary = String(form.barcode || "").trim();
    if (bc === primary) {
      setForm((f) => ({ ...f, barcode: "" }));
      return;
    }
    setExtraBarcodes((prev) => (Array.isArray(prev) ? prev : []).filter((x) => String(x || "").trim() !== bc));
  };

  const onSave = async () => {
    const primaryBarcode = String(form.barcode || "").trim();
    const patch = {
      name: String(form.name || "").trim(),
      barcode: primaryBarcode,
      description: String(form.description || ""),
      cost_price: normalizeNumberInput(form.cost_price),
      wholesale_price: normalizeNumberInput(form.wholesale_price),
      self_cost_price: normalizeNumberInput(form.self_cost_price),
      is_quick: Boolean(form.is_quick),
      is_active: Boolean(form.is_active),
      show_in_cashback: Boolean(form.show_in_cashback),
      category_ids: (selectedCategories || []).map((c) => c.id),
    };

    if (patch.wholesale_price === null) delete patch.wholesale_price;

    if (primaryBarcode) {
      patch.barcodes = uniqStrings([primaryBarcode, ...extraBarcodes]);
    }

    const res = await dispatch(updateProduct({ productId: id, patch }));
    if (updateProduct.fulfilled.match(res)) {
      // stay on page; show updated data
      dispatch(fetchProductDetail({ productId: id }));
    }
  };

  const onDelete = async () => {
    const res = await dispatch(deleteProduct({ productId: id }));
    if (deleteProduct.fulfilled.match(res)) {
      navigate("/warehouse/products");
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>Редактирование товара</div>
          <div style={styles.subTitle}>
            Товар <span style={styles.mono}>#{id}</span> (данные из <span style={styles.mono}>/api/product/products/</span>).
          </div>
        </div>

        <div style={styles.headerActions}>
          <button
            type="button"
            onClick={() => {
              const from = location.state?.from;
              if (typeof from === "string" && from.startsWith("/")) {
                navigate(from);
                return;
              }
              if (window.history.length > 1) {
                navigate(-1);
                return;
              }
              navigate("/warehouse/products");
            }}
            style={styles.btnGhost}
          >
            ← Назад
          </button>
          <button type="button" onClick={() => dispatch(fetchProductDetail({ productId: id }))} style={styles.btn}>
            Обновить
          </button>
          <button type="button" onClick={() => setLayoutModalOpen(true)} style={styles.btn} title="Настройки печати">
            Настройки печати
          </button>
          <button
            type="button"
            onClick={onPrintLabel}
            style={{ ...styles.btn, ...(canPrint ? null : styles.btnDisabled) }}
            disabled={!canPrint}
            title={!canPrint ? "Нужен штрихкод и загруженные данные" : "Печать ценника"}
          >
            <span style={styles.printIcon} aria-hidden="true">
              {/* printer icon */}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M7 8V3h10v5M7 17H6a3 3 0 0 1-3-3v-3a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v3a3 3 0 0 1-3 3h-1"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M7 14h10v7H7v-7Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            Печать ({printQty})
          </button>
          <button type="button" onClick={onSave} style={styles.btnPrimary} disabled={!canSave}>
            {saveStatus === "saving" ? "Сохранение…" : "Сохранить"}
          </button>
          <button
            type="button"
            onClick={() => setConfirmDeleteOpen(true)}
            style={{ ...styles.btnDanger, ...(loadStatus === "succeeded" ? null : styles.btnDisabled) }}
            disabled={loadStatus !== "succeeded" || deleteStatus === "saving"}
            title="Удалить товар (soft-delete)"
          >
            {deleteStatus === "saving" ? "Удаление…" : "Удалить"}
          </button>
        </div>
      </div>

      <ConfirmModal
        open={confirmDeleteOpen}
        title="Удалить товар?"
        message="Товар будет скрыт из всех списков и снят с продажи. Он останется в истории продаж, закупок и ревизий. Продолжить?"
        confirmLabel="Удалить"
        danger
        onConfirm={() => {
          setConfirmDeleteOpen(false);
          onDelete();
        }}
        onCancel={() => setConfirmDeleteOpen(false)}
      />

      {/* Print-only label via Portal → прямой потомок body → display:none CSS работает */}
      <style>{printCss}</style>
      {printNowQty !== null && createPortal(
        <div id="print-label-root" style={{ position: "fixed", left: -10000, top: 0 }}>
          {Array.from({ length: printNowQty }, (_, idx) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={idx}
              className="print-label"
              style={{ ...styles.printLabel, width: `${printWidthMm}mm`, height: `${printHeightMm}mm` }}
            >
              <div style={styles.printCompany}>AUF</div>
              <div style={styles.printName} title={printName}>{printName || "—"}</div>
              <div style={styles.printBarcode}>
                {printBarcode ? (
                  <Barcode
                    value={printBarcode}
                    height={28}
                    width={1.0}
                    margin={0}
                    displayValue
                    background="#ffffff"
                    lineColor="#000000"
                    textAlign="center"
                    fontSize={12}
                  />
                ) : (
                  <div style={styles.printMuted}>—</div>
                )}
              </div>
              <div style={styles.printPrices}>
                <div style={styles.printPriceRow}>
                  <span>Цена</span>
                  <b>{retailPrice == null ? "—" : retailPrice.toFixed(2)}</b>
                </div>
              </div>
            </div>
          ))}
        </div>,
        document.body
      )}

      {loadStatus === "loading" && <Loading text="Загрузка…" />}
      {loadStatus === "failed" && <div style={styles.error}>{loadError}</div>}
      {saveStatus === "failed" && <div style={styles.error}>{String(saveError || "Failed to save")}</div>}

      <div style={styles.body}>
        <div style={styles.card}>
          <div style={styles.grid}>
            <div style={styles.field}>
              <div style={styles.label}>ID</div>
              <div style={styles.valueMono}>{product?.id ?? "—"}</div>
            </div>
            <div style={styles.field}>
              <div style={styles.label}>Создан</div>
              <div style={styles.value}>{formatDateTime(product?.created_at)}</div>
            </div>
            <div style={styles.field}>
              <div style={styles.label}>Категории</div>
              <div style={styles.value}>{categoryNames.length ? categoryNames.join(", ") : "—"}</div>
            </div>
            <div style={styles.field}>
              <div style={styles.label}>Штрихкоды</div>
              <div style={styles.valueMono}>{barcodes.length ? barcodes.join(", ") : "—"}</div>
            </div>
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.formGrid}>
            <div style={styles.formFieldWide}>
              <div style={styles.label}>Категории (поиск, несколько)</div>
              <CategoryMultiSelect value={selectedCategories} onChange={setSelectedCategories} />
            </div>

            <div style={styles.formField}>
              <div style={styles.label}>Название</div>
              <input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                style={styles.input}
                placeholder="Название товара"
              />
            </div>

            <div style={styles.formField}>
              <div style={styles.label}>Штрихкод</div>
              <input
                value={form.barcode}
                onChange={(e) => setForm((f) => ({ ...f, barcode: e.target.value }))}
                style={styles.input}
                placeholder="Основной штрихкод"
              />
            </div>

            <div style={styles.formField}>
              <div style={styles.label}>Стоимость</div>
              <input
                value={String(form.cost_price ?? "")}
                onChange={(e) => setForm((f) => ({ ...f, cost_price: e.target.value }))}
                style={styles.input}
                placeholder="например 100.00"
              />
            </div>

            <div style={styles.formField}>
              <div style={styles.label}>Оптовая цена (wholesale_price)</div>
              <input
                value={String(form.wholesale_price ?? "")}
                onChange={(e) => setForm((f) => ({ ...f, wholesale_price: e.target.value }))}
                style={styles.input}
                placeholder="например 120.00"
              />
            </div>

            <div style={styles.formField}>
              <div style={styles.label}>Себестоимость (self_cost_price)</div>
              <input
                value={String(form.self_cost_price ?? "")}
                onChange={(e) => setForm((f) => ({ ...f, self_cost_price: e.target.value }))}
                style={styles.input}
                placeholder="например 95.00"
              />
            </div>

            <div style={styles.formFieldWide}>
              <div style={styles.label}>Описание</div>
              <textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                style={styles.textarea}
                placeholder="Описание"
                rows={4}
              />
            </div>

            <div style={styles.formField}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={Boolean(form.is_quick)}
                  onChange={(e) => setForm((f) => ({ ...f, is_quick: e.target.checked }))}
                />{" "}
                <span>Быстрый товар (is_quick)</span>
              </label>
            </div>

            <div style={styles.formField}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={Boolean(form.is_active)}
                  onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                />{" "}
                <span>Активный (is_active)</span>
              </label>
            </div>

            <div style={styles.formField}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={Boolean(form.show_in_cashback)}
                  onChange={(e) => setForm((f) => ({ ...f, show_in_cashback: e.target.checked }))}
                />{" "}
                <span>Показывать в auf cashback</span>
              </label>
            </div>

            <div style={styles.formFieldWide}>
              <div style={styles.label}>Доп. штрихкоды</div>
              <div style={styles.inlineRow}>
                <input
                  value={newBarcode}
                  onChange={(e) => setNewBarcode(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addBarcode();
                    }
                  }}
                  style={styles.input}
                  placeholder="Добавить доп. штрихкод…"
                />
                <button type="button" onClick={addBarcode} style={styles.btn} disabled={!String(newBarcode || "").trim()}>
                  + Добавить
                </button>
              </div>

              <div style={{ marginTop: 10 }}>
                <div style={styles.label}>Предпросмотр штрихкодов</div>
                {barcodes.length === 0 ? (
                  <div style={styles.hint}>Штрихкоды отсутствуют</div>
                ) : (
                  <div style={styles.barcodeRow}>
                    {barcodes.map((bc) => (
                      <BarcodePreview key={bc} value={bc} onDelete={deleteBarcode} />
                    ))}
                  </div>
                )}
              </div>

              <div style={styles.hintSmall}>
                Сохранение идет через <span style={styles.mono}>PATCH /api/product/products/{id}/</span> полем{" "}
                <span style={styles.mono}>barcodes</span>.
              </div>
            </div>
          </div>
        </div>

        <div style={styles.card}>
          <div style={{ ...styles.label, marginBottom: 10 }}>ФОТОГРАФИИ ТОВАРА</div>
          <ProductImageGallery productId={id} images={product?.images || []} />
        </div>
      </div>

      {layoutModalOpen && (
        <div style={styles.modalOverlay} onMouseDown={() => setLayoutModalOpen(false)}>
          <div style={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>Редактор макета печати</div>
              <button type="button" onClick={() => setLayoutModalOpen(false)} style={styles.modalClose} title="Закрыть">
                ×
              </button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.modalSettingsGrid}>
                <div style={styles.modalSetting}>
                  <div style={styles.label}>Размер этикетки (мм)</div>
                  <div style={styles.modalSettingRow}>
                    <input
                      type="number"
                      value={printSettings.widthMm}
                      min={20}
                      max={120}
                      onChange={(e) => setPrintSettings((s) => ({ ...s, widthMm: e.target.value }))}
                      style={styles.printInput}
                    />
                    <span style={styles.printControlsSep}>×</span>
                    <input
                      type="number"
                      value={printSettings.heightMm}
                      min={15}
                      max={120}
                      onChange={(e) => setPrintSettings((s) => ({ ...s, heightMm: e.target.value }))}
                      style={styles.printInput}
                    />
                  </div>
                </div>
                <div style={styles.modalSetting}>
                  <div style={styles.label}>Количество</div>
                  <input
                    type="number"
                    value={printSettings.qty}
                    min={1}
                    max={100}
                    onChange={(e) => setPrintSettings((s) => ({ ...s, qty: e.target.value }))}
                    style={styles.printInputQty}
                  />
                </div>
                <div style={styles.modalSetting}>
                  <div style={styles.label}>Ориентация</div>
                  <Select
                    value={labelLayout.orientation || "portrait"}
                    onChange={(v) => setLabelLayout((p) => ({ ...p, orientation: v }))}
                    options={[
                      { value: "portrait", label: "Вертикальная" },
                      { value: "landscape", label: "Горизонтальная" },
                    ]}
                    isSearchable={false}
                  />
                </div>
              </div>
              <div style={styles.modalHint}>
                Размер этикетки: <span style={styles.mono}>{printWidthMm}×{printHeightMm}мм</span>
              </div>
              <div style={styles.modalHintSmall}>
                <b>Важно:</b> В диалоге печати браузера (Ctrl+P) поставь <span style={styles.mono}>Copies/Копии = 1</span>.<br />
                Количество этикеток ({printQty} шт) задаётся в поле "Количество" выше, а не в диалоге браузера!
              </div>
              <div style={styles.modalPreviewWrap}>
                <div
                  style={{
                    ...styles.printPreviewPage,
                    width: printWidthMm * 3.78, // 1mm ≈ 3.78px at 96 DPI
                    height: printHeightMm * 3.78,
                  }}
                >
                  <div style={getContentStyle(labelLayout.orientation, false)}>
                    <div style={styles.printCompany}>AUF</div>
                    <div style={styles.printName} title={printName}>
                      {printName || "—"}
                    </div>
                    <div style={styles.printBarcode}>
                      {printBarcode ? (
                        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%" }}>
                          <Barcode
                            value={printBarcode}
                            height={28}
                            width={1.0}
                            margin={0}
                            displayValue
                            background="#ffffff"
                            lineColor="#000000"
                            textAlign="center"
                            fontSize={12}
                          />
                        </div>
                      ) : (
                        <div style={styles.printMuted}>—</div>
                      )}
                    </div>
                    <div style={styles.printPrices}>
                      <div style={styles.printPriceRow}>
                        <span>Цена</span>
                        <b>{retailPrice == null ? "—" : retailPrice.toFixed(2)}</b>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div style={styles.modalActions}>
              <button
                type="button"
                onClick={() => {
                  setPrintSettings({ widthMm: 50, heightMm: 40, qty: 1 });
                  setLabelLayout({ orientation: "portrait" });
                }}
                style={styles.btn}
                title="Вернуть настройки по умолчанию"
              >
                Сброс настроек
              </button>
              <button type="button" onClick={() => setLayoutModalOpen(false)} style={styles.btnPrimary}>
                Готово
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

const styles = {
  page: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    background: "var(--c-bg)",
    color: "var(--c-text)",
  },
  mono: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
  },
  header: {
    padding: "16px 18px",
    borderBottom: "1px solid var(--c-border)",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 14,
    flexWrap: "wrap",
  },
  title: { fontSize: 20, fontWeight: 900 },
  subTitle: { marginTop: 6, opacity: 0.75, fontSize: 14, lineHeight: 1.35, maxWidth: 760 },
  headerActions: { display: "flex", gap: 10, alignItems: "center" },
  printControls: {
    display: "grid",
    gap: 6,
    padding: "8px 10px",
    borderRadius: 12,
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
  },
  printControlsRow: { display: "flex", alignItems: "center", gap: 6 },
  printControlsLabel: { fontSize: 13, opacity: 0.75, fontWeight: 900 },
  printControlsSep: { opacity: 0.6, fontWeight: 900 },
  printControlsUnit: { opacity: 0.7, fontWeight: 900, fontSize: 13 },
  printInput: {
    width: 64,
    padding: "8px 10px",
    borderRadius: 10,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 14,
    fontWeight: 900,
  },
  printInputQty: {
    width: 64,
    padding: "8px 10px",
    borderRadius: 10,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 14,
    fontWeight: 900,
  },
  btnTiny: {
    padding: "6px 10px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 14,
  },
  printIcon: { display: "inline-flex", alignItems: "center" },
  btn: {
    padding: "10px 12px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 15,
  },
  btnDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  btnPrimary: {
    padding: "10px 12px",
    borderRadius: 99,
    border: "1px solid rgba(245,197,24,0.3)",
    background: "var(--c-btn)",
    color: "var(--c-btn-text)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 15,
    opacity: 1,
  },
  btnGhost: {
    padding: "10px 12px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "transparent",
    color: "var(--c-text-sub)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 15,
  },
  btnDanger: {
    padding: "10px 12px",
    borderRadius: 99,
    border: "1px solid rgba(220,38,38,0.35)",
    background: "rgba(220,38,38,0.12)",
    color: "var(--c-danger)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 15,
  },
  notice: { padding: "12px 18px", fontSize: 15, opacity: 0.85 },
  error: {
    padding: "12px 18px",
    fontSize: 15,
    background: "var(--c-danger-dim)",
    color: "var(--c-danger)",
    borderTop: "1px solid var(--c-danger)",
    borderBottom: "1px solid var(--c-danger)",
  },
  body: { padding: 18, minHeight: 0, overflow: "auto", display: "grid", gap: 12 },
  card: {
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    borderRadius: 16,
    padding: 14,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 12,
  },
  field: { display: "grid", gap: 6 },
  label: { fontSize: 13, color: "var(--c-text-dim)", fontWeight: 900 },
  value: { fontSize: 15 },
  valueMono: {
    fontSize: 15,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
  },
  formField: { display: "grid", gap: 6 },
  formFieldWide: { display: "grid", gap: 6, gridColumn: "1 / -1" },
  inlineRow: { display: "flex", gap: 10, alignItems: "center" },
  input: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 15,
  },
  hint: { fontSize: 15, opacity: 0.75, fontWeight: 800 },
  hintSmall: { fontSize: 14, opacity: 0.75, fontWeight: 800, marginTop: 6 },
  // --- print label ---
  // Ценник печатается на белой бумаге: белый фон и чёрный текст здесь
  // намеренно захардкожены и НЕ должны переезжать на токены темы.
  printPreviewPage: {
    position: "relative",
    marginTop: 10,
    borderRadius: 12,
    border: "1px solid var(--c-border)",
    background: "#ffffff",
    overflow: "hidden",
  },
  printRootWrap: {
    position: "fixed",
    left: -10000,
    top: 0,
  },
  printLabel: {
    position: "relative",
    padding: 0,
    boxSizing: "border-box",
    background: "#ffffff",
    color: "#000000",
    fontFamily: "Arial, sans-serif",
    overflow: "hidden",
  },
  printCompany: {
    position: "absolute", top: "0mm", left: "1mm", right: "1mm", height: "5mm",
    overflow: "hidden",
    fontSize: 16, fontWeight: 900, letterSpacing: 0.5,
    textAlign: "center",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  printName: {
    position: "absolute", top: "5mm", left: "1mm", right: "1mm", height: "7mm",
    overflow: "hidden",
    fontSize: 13, fontWeight: 700, lineHeight: 1.1,
    textAlign: "center", wordBreak: "break-word",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  printBarcode: {
    position: "absolute", top: "12mm", left: "1mm", right: "1mm", height: "12mm",
    overflow: "hidden",
    display: "flex", justifyContent: "center", alignItems: "flex-start",
  },
  printPrices: {
    position: "absolute", top: "24mm", left: "1mm", right: "1mm", height: "6mm",
    overflow: "hidden",
    fontSize: 16, fontWeight: 900, textAlign: "center",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  printPriceRow: { display: "flex", justifyContent: "center", gap: 6, fontWeight: 900 },
  printMuted: { opacity: 0.6, fontWeight: 700 },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    backdropFilter: "blur(6px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 200,
    padding: 18,
  },
  modal: {
    width: "min(980px, 96vw)",
    maxHeight: "90vh",
    overflow: "auto",
    borderRadius: 16,
    border: "1px solid var(--c-border)",
    background: "var(--c-bg)",
    color: "var(--c-text)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
  },
  modalHeader: {
    padding: "14px 16px",
    borderBottom: "1px solid var(--c-border)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  modalTitle: { fontSize: 16, fontWeight: 950 },
  modalClose: {
    width: 34,
    height: 34,
    borderRadius: 16,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 20,
    lineHeight: 1,
  },
  modalBody: { padding: 16, display: "grid", gap: 12 },
  modalHint: { fontSize: 15, opacity: 0.85, fontWeight: 800 },
  modalHintSmall: { fontSize: 14, opacity: 0.75, fontWeight: 800 },
  modalSettingsGrid: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  },
  modalSetting: { display: "grid", gap: 6 },
  modalSettingRow: { display: "flex", alignItems: "center", gap: 6 },
  modalPreviewWrap: {
    display: "flex",
    justifyContent: "center",
    padding: 12,
    borderRadius: 16,
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
  },
  modalActions: {
    padding: "14px 16px",
    borderTop: "1px solid var(--c-border)",
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    flexWrap: "wrap",
  },
  barcodeRow: { display: "flex", flexDirection: "column", gap: 12, alignItems: "flex-start" },
  barcodeCard: {
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    borderRadius: 16,
    padding: 10,
    width: "80%",
  },
  barcodeItemRow: { display: "flex", alignItems: "center", gap: 12, width: "100%" },
  barcodeLabel: {
    width: 220,
    maxWidth: "40vw",
    fontSize: 14,
    fontWeight: 900,
    opacity: 0.9,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  barcodeWhite: {
    flex: 1,
    background: "#ffffff",
    borderRadius: 10,
    padding: "10px 12px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  barcodeDeleteBtn: {
    width: 34,
    height: 34,
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    cursor: "pointer",
    fontWeight: 900,
    lineHeight: 1,
  },
  textarea: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 15,
    resize: "vertical",
  },
  checkboxLabel: { fontSize: 15, fontWeight: 800, display: "flex", gap: 10, alignItems: "center" },
};

